import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Badge,
  Tooltip,
  message,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import {
  type KrybotAgent,
  type AgentType,
  krybotAgentTypeTagColor,
} from '../../../types/krybot';
import { AgentTypeSelectOptions } from '../AgentTypeSelectOptions';
import { AgentDetail } from './AgentDetail';
import { AgentForm } from './AgentForm';
import styles from './AgentManagement.module.css';

const { Search } = Input;
const { Option } = Select;

interface AgentListProps {
  onRefresh?: () => void;
}

export const AgentList: React.FC<AgentListProps> = ({ onRefresh }) => {
  const [agents, setAgents] = useState<KrybotAgent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentTypeFilter, setAgentTypeFilter] = useState<AgentType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedAgent, setSelectedAgent] = useState<KrybotAgent | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<KrybotAgent | null>(null);

  useEffect(() => {
    loadAgents();
  }, [agentTypeFilter, statusFilter]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: { is_active?: boolean; agent_type?: AgentType } = {};
      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }
      if (agentTypeFilter !== 'all') {
        params.agent_type = agentTypeFilter;
      }

      const data = await KrybotService.listAgents(params);
      setAgents(data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load agents';
      setError(errorMsg);
      message.error(`Failed to load agents: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgent(null);
    setShowForm(true);
  };

  const handleEdit = (agent: KrybotAgent) => {
    setEditingAgent(agent);
    setShowForm(true);
  };

  const handleView = (agent: KrybotAgent) => {
    setSelectedAgent(agent);
    setShowDetail(true);
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingAgent(null);
    if (saved) {
      loadAgents();
      onRefresh?.();
    }
  };

  const handleDetailClose = () => {
    setShowDetail(false);
    setSelectedAgent(null);
  };

  const filteredAgents = agents.filter((agent) => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        agent.name.toLowerCase().includes(searchLower) ||
        agent.display_name?.toLowerCase().includes(searchLower) ||
        agent.description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: KrybotAgent) => (
        <Space>
          <Button type="link" onClick={() => handleView(record)}>
            {record.display_name || text}
          </Button>
          <Tag color={record.is_active ? 'green' : 'default'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'agent_type',
      key: 'agent_type',
      render: (type: AgentType | undefined) => {
        if (!type) return <Tag>Unknown</Tag>;
        return <Tag color={krybotAgentTypeTagColor(type)}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, record: KrybotAgent) => (
        <Space>
          <Badge status={record.is_active ? 'success' : 'default'} text={record.is_active ? 'Active' : 'Inactive'} />
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: KrybotAgent) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="agent-list">
      <div className="agent-list-header">
        <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
          <Space>
            <Search
              placeholder="Search agents..."
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Select
              value={agentTypeFilter}
              onChange={setAgentTypeFilter}
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
            >
              <Option value="all">All Types</Option>
              <AgentTypeSelectOptions />
            </Select>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 150 }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadAgents}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Create Agent
            </Button>
          </Space>
        </Space>
      </div>

      {error && (
        <div style={{ marginBottom: 16, color: 'red' }}>
          Error: {error}
        </div>
      )}

      <Table
        columns={columns}
        dataSource={filteredAgents}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} agents`,
        }}
      />

      {showDetail && selectedAgent && (
        <Modal
          title={`Agent: ${selectedAgent.display_name || selectedAgent.name}`}
          open={showDetail}
          onCancel={handleDetailClose}
          footer={null}
          width={1200}
          style={{ top: 20 }}
        >
          <AgentDetail agent={selectedAgent} onRefresh={loadAgents} />
        </Modal>
      )}

      {showForm && (
        <Modal
          title={editingAgent ? 'Edit Agent' : 'Create Agent'}
          open={showForm}
          onCancel={() => handleFormClose(false)}
          footer={null}
          width={800}
        >
          <AgentForm
            agent={editingAgent}
            onSave={() => handleFormClose(true)}
            onCancel={() => handleFormClose(false)}
          />
        </Modal>
      )}
    </div>
  );
};

